import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import {
  addRaPaymentToSchedule,
  getScheduledPaymentById,
  unscheduleItem,
} from "../../api/scheduledPayments";
import { getReceiptAcknowledgements } from "../../api/reciptAcknowledgement";
import { getRaPaymentsByReceipt, updateRaPayment } from "../../api/raPayments";
import { formatCurrency } from "../../services/valueFormatters";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

export default function ScheduledPaymentItemsView({ props }) {
  const { id } = props;
  const { openSnack } = useSnack();

  const [scheduledPayment, setScheduledPayment] = useState(null);
  const [loading, setLoading] = useState(false);

  // "Add row" picker state
  const [addReceipt, setAddReceipt] = useState(null);
  const [addInstallments, setAddInstallments] = useState([]);
  const [addRaPaymentId, setAddRaPaymentId] = useState("");

  const loadScheduledPayment = async () => {
    const response = await getScheduledPaymentById(id);
    setScheduledPayment(response.data);
  };

  useEffect(() => {
    loadScheduledPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!scheduledPayment) return null;

  const items = scheduledPayment.items || [];

  const handleDateChange = async (item, newDate) => {
    if (!newDate) return;
    try {
      setLoading(true);
      await updateRaPayment(item.raPaymentId, {
        paymentConditionId: item.raPayment?.paymentConditionId ?? null,
        amount: item.raPayment?.amount,
        paymentDate: newDate,
      });
      openSnack("Fecha de pago actualizada exitosamente", "success");
      await loadScheduledPayment();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRow = async (item) => {
    if (!window.confirm("¿Seguro que quieres quitar este renglón de la corrida?")) return;
    try {
      setLoading(true);
      await unscheduleItem(id, item.id);
      openSnack("Renglón desprogramado exitosamente", "success");
      await loadScheduledPayment();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddReceiptChange = async (receipt) => {
    setAddReceipt(receipt);
    setAddRaPaymentId("");
    if (!receipt) {
      setAddInstallments([]);
      return;
    }
    const response = await getRaPaymentsByReceipt(receipt.id);
    setAddInstallments(response.data);
  };

  const handleAddRow = async () => {
    if (!addRaPaymentId) return;
    try {
      setLoading(true);
      await addRaPaymentToSchedule(id, addRaPaymentId);
      openSnack("Cuota agregada a la corrida exitosamente", "success");
      setAddReceipt(null);
      setAddInstallments([]);
      setAddRaPaymentId("");
      await loadScheduledPayment();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={1} minWidth={600}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Descripción / Contrarrecibo</TableCell>
            <TableCell>Cuota</TableCell>
            <TableCell>Fecha de pago</TableCell>
            <TableCell align="right">Monto</TableCell>
            <TableCell align="center">Quitar</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const dateDisabled = Boolean(
              item.raPayment?.paymentCondition &&
                !item.raPayment.paymentCondition.requiresManualDate
            );
            return (
              <TableRow key={item.id}>
                <TableCell>
                  {item.raPayment?.receiptAcknowledgement?.code || item.description || "—"}
                </TableCell>
                <TableCell>
                  {item.raPayment ? `Cuota ${item.raPayment.installmentNo}` : "Sin vincular"}
                </TableCell>
                <TableCell>
                  {item.raPayment ? (
                    <TextField
                      type="date"
                      size="small"
                      value={item.raPayment.paymentDate || ""}
                      disabled={dateDisabled || loading}
                      onChange={(e) => handleDateChange(item, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    disabled={loading}
                    onClick={() => handleDeleteRow(item)}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" mb={1}>
        Agregar cuota a la corrida
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box flex={1}>
          <LGAutocomplete
            fetchFunction={getReceiptAcknowledgements}
            fetchBy="code"
            queryParams={{
              companyId: scheduledPayment.companyId,
              providerCode: scheduledPayment.provider?.code,
            }}
            optionLabel="code"
            value={addReceipt}
            onChange={(event, value) => handleAddReceiptChange(value)}
            label="Contrarrecibo"
          />
        </Box>
        <FormControl sx={{ minWidth: 260 }} disabled={!addReceipt}>
          <InputLabel>Cuota</InputLabel>
          <Select
            label="Cuota"
            value={addRaPaymentId}
            onChange={(e) => setAddRaPaymentId(e.target.value)}
          >
            {addInstallments.map((installment) => (
              <MenuItem key={installment.id} value={installment.id}>
                Cuota {installment.installmentNo} — {installment.amount} — {installment.paymentDate}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          disabled={!addRaPaymentId || loading}
          onClick={handleAddRow}
        >
          Agregar
        </Button>
      </Stack>
    </Box>
  );
}
